import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const BASE = `${API_URL}/admin/identity-repair`;

export type StepKey = "diagnose" | "normalize" | "sweepDry" | "sweepApply" | "syncEvents" | "orphans" | "purge" | "reset";
export type StepStatus = "idle" | "running" | "done" | "error" | "stopped";

export interface StepDefinition {
    key: StepKey;
    order: number;
    title: string;
    blurb: string;
    writes: boolean;
}

export interface PassStats {
    sirensChecked: number;
    establishmentsConfirmed: number;
    establishmentsUpdated: number;
    noOwnerFound: number;
    notFound: number;
    errors: number;
    /** Answered from the SIRENE stock instead of the registry — no API call spent. */
    skippedFromBulk: number;
}

export interface JobState {
    step: StepKey;
    status: StepStatus;
    startedAt: string | null;
    finishedAt: string | null;
    processed: number;
    total: number;
    logs: { at: string; level: "info" | "warn"; message: string }[];
    stats: PassStats | null;
    result: any | null;
    error: string | null;
    /** Present on the simulation only; /status reports the count, not the payload. */
    sampleCount?: number;
}

export interface HeavyPerson {
    personId: string;
    name: string;
    count: number;
    birthdate: string | null;
}

export interface Overview {
    establishments: { total: number; withSiren: number; pending: number; checked: number };
    people: {
        total: number;
        withBirthdate: number;
        withoutBirthdate: number;
        dirigeants: number;
        dirigeantsWithBirthdate: number;
        dirigeantsWithoutBirthdate: number;
        others: number;
    };
    heavy: HeavyPerson[];
}

export async function getSteps(): Promise<StepDefinition[]> {
    const { data } = await axios.get(`${BASE}/steps`, { headers: authHeaders() });
    return data.steps;
}

export async function getStatus(): Promise<{ active: StepKey | null; steps: JobState[] }> {
    const { data } = await axios.get(`${BASE}/status`, { headers: authHeaders() });
    return data;
}

export async function getOverview(): Promise<Overview> {
    const { data } = await axios.get(`${BASE}/overview`, { headers: authHeaders() });
    return data;
}

export async function runStep(
    step: StepKey,
    params: { budgetMinutes?: number } = {}
): Promise<void> {
    await axios.post(`${BASE}/steps/${step}/run`, params, { headers: authHeaders() });
}

/**
 * Pulls the simulation's per-company records and hands them to the browser as a file.
 *
 * Built and revoked client-side on purpose: nothing is written on the server, and the blob is
 * released as soon as the download starts, so leaving the page loses it — which is what makes
 * it safe to dump raw registry data into.
 */
export async function downloadSamples(step: StepKey): Promise<number> {
    const { data } = await axios.get(`${BASE}/steps/${step}/samples`, { headers: authHeaders() });
    // The leading BOM and explicit charset are what stop accented names arriving as "PrÃ©sident"
    // when the file is opened by a tool that would otherwise assume the system codepage.
    const blob = new Blob(["﻿" + JSON.stringify(data, null, 2)], {
        type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `identity-repair-${step}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return data.count ?? 0;
}

export async function stopRun(): Promise<void> {
    await axios.post(`${BASE}/stop`, {}, { headers: authHeaders() });
}
